import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import "../../styles/AdminStyling/EditProjects.css";
import Select from "react-select";

// Helper: Parse PIndex to extract timeline [semester, year]
const extractTimelineFromIndex = (pindex) => {
  if (!pindex) return ["", ""];
  const match = pindex.match(/(\d{2})(sp|su|fa)/i);
  if (!match) return ["", ""];
  const [, yearPrefix, seasonCode] = match;
  const year = `20${yearPrefix}`;
  const seasonMap = { sp: "Spring", su: "Summer", fa: "Fall" };
  const season = seasonMap[seasonCode.toLowerCase()];
  return season ? [season, year] : ["", ""];
};

// Build year options from 2003 to current
const buildYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const options = [];
  for (let y = 2003; y <= currentYear; y++) {
    options.push({ value: y.toString(), label: y.toString() });
  }
  return options;
};

const semesterOptions = [
  { value: "Spring", label: "Spring" },
  { value: "Summer", label: "Summer" },
  { value: "Fall", label: "Fall" },
];
const yearOptions = buildYearOptions();

const EditProjects = ({ activeTab }) => {
  const NextPage = `${process.env.PUBLIC_URL}/assets/logos/next-page.svg`;
  const NextPageDark = `${process.env.PUBLIC_URL}/assets/logos/next-page-dark.svg`;
  const AddUser = `${process.env.PUBLIC_URL}/assets/logos/add-user.svg`;
  const EditIcon = `${process.env.PUBLIC_URL}/assets/logos/edit-icon.svg`;
  const DeleteIcon = `${process.env.PUBLIC_URL}/assets/logos/delete-icon.svg`;

  const [projects, setProjects] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showUnset, setShowUnset] = useState(false);
  const [unsetIndices, setUnsetIndices] = useState([]);
  const [brokenProjects, setBrokenProjects] = useState([]);
  const [showBroken, setShowBroken] = useState(false);
  const [sponsors, setSponsors] = useState([]);
  const containerRef = useRef(null);

  // --- Add project states ---
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({
    PName: "",
    PIndex: "",
    SPTag: [],
    PTimeline: ["", ""],
    PDescription: "",
    PPoC: [],
    SPPoC: [],
  });

  // --- Edit project states ---
  const [expandedProjects, setExpandedProjects] = useState([]);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editForm, setEditForm] = useState({
    PID: null,
    PName: "",
    PIndex: "",
    SPTag: [],
    PTimeline: ["", ""],
    PDescription: "",
    PPoC: [],
    SPPoC: [],
  });

  // Track unsaved changes
  const [addDirty, setAddDirty] = useState(false);
  const [editDirty, setEditDirty] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  // Deletion confirmation (modal overlay)
  const [confirmDeleteProject, setConfirmDeleteProject] = useState(null);
  const [deleteInput, setDeleteInput] = useState("");

  // --- Filter states ---
  const [sponsorFilter, setSponsorFilter] = useState([]);
  const [semesterFilter, setSemesterFilter] = useState(null);
  const [yearFilter, setYearFilter] = useState(null);

  // ------------------------------
  // 1. Lifecycle: Fetch data
  // ------------------------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projRes, unsetRes, sponsorRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/getSetProjects`),
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/getUnsetProjects`
          ),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/sponsors`),
        ]);
        const brokenRes = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/getBrokenProjects`
        );
        if (projRes.data.success) setProjects(projRes.data.projects || []);
        if (unsetRes.data.success) {
          const sortedUnset = (unsetRes.data.unsetIndices || []).sort();
          setUnsetIndices(sortedUnset);
        }
        if (sponsorRes.data.success) {
          const sponsorOptions = sponsorRes.data.sponsors.map((s) => ({
            value: s.spname,
            label: s.spname,
          }));
          setSponsors(sponsorOptions);
        }
        if (brokenRes.data.success) {
          setBrokenProjects(brokenRes.data.brokenProjects);
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };
    fetchData();
  }, []);

  // Warn user if unsaved changes
  useEffect(
    () => setUnsavedChanges(addDirty || editDirty),
    [addDirty, editDirty]
  );

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (unsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    if (unsavedChanges)
      window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [unsavedChanges]);

  // Close edit if click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (expandedProjects.length > 0) {
        if (containerRef.current && !containerRef.current.contains(e.target)) {
          setExpandedProjects([]);
          if (editingProjectId) cancelEdit();
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [expandedProjects, editingProjectId]);

  // ------------------------------
  // 2. Add new project
  // ------------------------------
  const startAdd = (index = "") => {
    setAdding(true);
    const timeline = extractTimelineFromIndex(index);
    setAddForm({
      PName: "",
      PIndex: index || "",
      SPTag: [],
      PTimeline: timeline,
      PDescription: "",
      PPoC: [],
      SPPoC: [],
    });
    setAddDirty(false);
  };

  const cancelAdd = () => {
    setAdding(false);
    setAddForm({
      PName: "",
      PIndex: "",
      SPTag: [],
      PTimeline: ["", ""],
      PDescription: "",
      PPoC: [],
      SPPoC: [],
    });
    setAddDirty(false);
  };

  const handleAddChange = (field, value) => {
    if (field === "PIndex") {
      const timeline = extractTimelineFromIndex(value);
      setAddForm((prev) => ({ ...prev, PIndex: value, PTimeline: timeline }));
    } else {
      setAddForm((prev) => ({ ...prev, [field]: value }));
    }
    setAddDirty(true);
  };

  const handleAddTimelinePart = (partIndex, newValue) => {
    setAddForm((prev) => {
      const newTimeline = [...prev.PTimeline];
      newTimeline[partIndex] = newValue;
      return { ...prev, PTimeline: newTimeline };
    });
    setAddDirty(true);
  };

  // PoC functions for add form
  const addAddPoCItem = () => {
    setAddForm((prev) => ({ ...prev, PPoC: [...prev.PPoC, ""] }));
    setAddDirty(true);
  };
  const removeAddPoCItem = (index) => {
    setAddForm((prev) => {
      const newList = [...prev.PPoC];
      newList.splice(index, 1);
      return { ...prev, PPoC: newList };
    });
    setAddDirty(true);
  };
  const handleAddPoCChange = (index, value) => {
    setAddForm((prev) => {
      const newList = [...prev.PPoC];
      newList[index] = value;
      return { ...prev, PPoC: newList };
    });
    setAddDirty(true);
  };

  // PoC functions for add form
  const addAddSPPoCItem = () => {
    setAddForm((prev) => ({ ...prev, SPPoC: [...prev.SPPoC, ""] }));
    setAddDirty(true);
  };
  const removeAddSPPoCItem = (index) => {
    setAddForm((prev) => {
      const newList = [...prev.SPPoC];
      newList.splice(index, 1);
      return { ...prev, SPPoC: newList };
    });
    setAddDirty(true);
  };
  const handleAddSPPoCChange = (index, value) => {
    setAddForm((prev) => {
      const newList = [...prev.SPPoC];
      newList[index] = value;
      return { ...prev, SPPoC: newList };
    });
    setAddDirty(true);
  };

  const saveNewProject = () => {
    const { PName, PIndex, SPTag } = addForm;
    if (!PName.trim() || !PIndex.trim() || SPTag.length === 0) {
      alert("PName, PIndex, and SPTag are required!");
      return;
    }
    performAdd();
  };

  const performAdd = async () => {
    try {
      const [sem, yr] = addForm.PTimeline;
      const timelineList = sem && yr ? [sem.trim(), yr.trim()] : null;
      const pocList = addForm.PPoC.map((p) => p.trim()).filter((p) => p);
      const sppocList = addForm.SPPoC.map((x) => x.trim()).filter((x) => x);
      const payload = {
        PName: addForm.PName,
        PIndex: addForm.PIndex,
        SPTag: addForm.SPTag.map((opt) => opt.value),
        PTimeline: timelineList,
        PDescription: addForm.PDescription,
        PPoC: pocList.length > 0 ? pocList : null,
        SPPoC: sppocList.length > 0 ? sppocList : null,
      };
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/addProject`,
        payload
      );
      if (res.data.success) {
        const projRes = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/getSetProjects`
        );
        if (projRes.data.success) setProjects(projRes.data.projects || []);
        const unsetRes = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/getUnsetProjects`
        );
        if (unsetRes.data.success)
          setUnsetIndices((unsetRes.data.unsetIndices || []).sort());
        cancelAdd();
      } else {
        alert("Failed to add project: " + res.data.message);
      }
    } catch (error) {
      console.error("Error adding project:", error);
    }
  };

  // ------------------------------
  // 3. Edit existing project
  // ------------------------------
  const toggleExpand = (pid) => {
    setExpandedProjects((prev) => {
      if (prev.includes(pid)) return [];
      return [pid];
    });
    if (editingProjectId && editingProjectId !== pid) cancelEdit();
  };

  const enableEdit = (project) => {
    if (!expandedProjects.includes(project.pid))
      setExpandedProjects([project.pid]);
    setEditingProjectId(project.pid);
    let timeline = ["", ""];
    if (project.ptimeline && project.ptimeline.length === 2) {
      timeline = [...project.ptimeline];
    } else if (project.ptimeline && project.ptimeline.length === 1) {
      const parts = project.ptimeline[0].split(" ");
      if (parts.length === 2) timeline = [parts[0], parts[1]];
    }
    setEditForm({
      PID: project.pid,
      PName: project.pname || "",
      PIndex: project.pindex || "",
      SPTag: project.sptag
        ? project.sptag.map((tag) => ({ value: tag, label: tag }))
        : [],
      PTimeline: timeline,
      PDescription: project.pdescription || "",
      PPoC: project.ppoc ? [...project.ppoc] : [],
      SPPoC: project.sppoc ? [...project.sppoc] : [],
    });
    setEditDirty(false);
  };

  const cancelEdit = () => {
    setEditingProjectId(null);
    setEditForm({
      PID: null,
      PName: "",
      PIndex: "",
      SPTag: [],
      PTimeline: ["", ""],
      PDescription: "",
      PPoC: [],
      SPPoC: [],
    });
    setEditDirty(false);
  };

  const handleEditChange = (field, value) => {
    if (field === "PIndex") {
      const timeline = extractTimelineFromIndex(value);
      setEditForm((prev) => ({ ...prev, PIndex: value, PTimeline: timeline }));
    } else {
      setEditForm((prev) => ({ ...prev, [field]: value }));
    }
    setEditDirty(true);
  };

  const handleEditTimelinePart = (partIndex, newValue) => {
    setEditForm((prev) => {
      const newTimeline = [...prev.PTimeline];
      newTimeline[partIndex] = newValue;
      return { ...prev, PTimeline: newTimeline };
    });
    setEditDirty(true);
  };

  // PoC functions for edit form
  const addEditPoCItem = () => {
    setEditForm((prev) => ({ ...prev, PPoC: [...prev.PPoC, ""] }));
    setEditDirty(true);
  };
  const removeEditPoCItem = (index) => {
    setEditForm((prev) => {
      const newList = [...prev.PPoC];
      newList.splice(index, 1);
      return { ...prev, PPoC: newList };
    });
    setEditDirty(true);
  };
  const handleEditPoCChange = (index, value) => {
    setEditForm((prev) => {
      const newList = [...prev.PPoC];
      newList[index] = value;
      return { ...prev, PPoC: newList };
    });
    setEditDirty(true);
  };

  // PoC functions for edit form
  const addEditSPPoCItem = () => {
    setEditForm((prev) => ({ ...prev, SPPoC: [...prev.SPPoC, ""] }));
    setEditDirty(true);
  };
  const removeEditSPPoCItem = (index) => {
    setEditForm((prev) => {
      const newList = [...prev.SPPoC];
      newList.splice(index, 1);
      return { ...prev, SPPoC: newList };
    });
    setEditDirty(true);
  };
  const handleEditSPPoCChange = (index, value) => {
    setEditForm((prev) => {
      const newList = [...prev.SPPoC];
      newList[index] = value;
      return { ...prev, SPPoC: newList };
    });
    setEditDirty(true);
  };

  const saveEdits = () => {
    if (
      !editForm.PName.trim() ||
      !editForm.PIndex.trim() ||
      editForm.SPTag.length === 0
    ) {
      alert("PName, PIndex, and SPTag are required!");
      return;
    }
    performEdit();
  };

  const performEdit = async () => {
    try {
      const [sem, yr] = editForm.PTimeline;
      const timelineList = sem && yr ? [sem.trim(), yr.trim()] : null;
      const pocList = editForm.PPoC.map((p) => p.trim()).filter((p) => p);
      const sppocList = editForm.SPPoC.map((x) => x.trim()).filter((x) => x);
      const payload = {
        PID: editForm.PID,
        PName: editForm.PName,
        PIndex: editForm.PIndex,
        SPTag: editForm.SPTag.map((opt) => opt.value),
        PTimeline: timelineList,
        PDescription: editForm.PDescription,
        PPoC: pocList.length > 0 ? pocList : null,
        SPPoC: sppocList.length > 0 ? sppocList : null,
      };
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/editProject`,
        payload
      );
      if (res.data.success) {
        const projRes = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/getSetProjects`
        );
        if (projRes.data.success) setProjects(projRes.data.projects || []);
        const unsetRes = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/getUnsetProjects`
        );
        if (unsetRes.data.success)
          setUnsetIndices((unsetRes.data.unsetIndices || []).sort());
        const brokenRes = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/getBrokenProjects`
        );
        if (brokenRes.data.success) {
          setBrokenProjects(brokenRes.data.brokenProjects || []);
        }
      } else {
        alert("Failed to save changes: " + res.data.message);
      }
    } catch (error) {
      console.error("Error saving project edits:", error);
    }
    cancelEdit();
  };

  const confirmDelete = (project) => {
    setConfirmDeleteProject({ pid: project.pid, pname: project.pname });
    setDeleteInput("");
  };

  const performDelete = async () => {
    if (!confirmDeleteProject) return;
    try {
      const pid = confirmDeleteProject.pid;
      const res = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/deleteProject?pid=${pid}`
      );
      if (res.data.success) {
        const projRes = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/getSetProjects`
        );
        if (projRes.data.success) setProjects(projRes.data.projects || []);
        const unsetRes = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/getUnsetProjects`
        );
        if (unsetRes.data.success)
          setUnsetIndices((unsetRes.data.unsetIndices || []).sort());
      } else {
        alert("Failed to delete project: " + res.data.message);
      }
    } catch (error) {
      console.error("Error deleting project:", error);
    }
    setConfirmDeleteProject(null);
    setDeleteInput("");
  };

  // Test Ccomment.
  // Filter projects
  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      if (sponsorFilter.length > 0) {
        const selectedSponsors = sponsorFilter.map((sf) => sf.value);
        if (
          !project.sptag ||
          !project.sptag.some((tag) => selectedSponsors.includes(tag))
        )
          return false;
      }
      if (semesterFilter) {
        if (
          !Array.isArray(project.ptimeline) ||
          project.ptimeline.length < 2 ||
          project.ptimeline[0] !== semesterFilter.value
        )
          return false;
      }
      if (yearFilter) {
        if (
          !Array.isArray(project.ptimeline) ||
          project.ptimeline.length < 2 ||
          project.ptimeline[1] !== yearFilter.value
        )
          return false;
      }
      return true;
    });
  }, [projects, sponsorFilter, semesterFilter, yearFilter]);

  // Reset page to 1 if filteredProjects change.
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredProjects]);

  // Sort the filtered projects alphabetically by name
  const sortedProjects = useMemo(() => {
    return filteredProjects
      .slice()
      .sort((a, b) => a.pname.localeCompare(b.pname));
  }, [filteredProjects]);

  // Calculate total pages
  const itemsPerPage = 10;
  const totalPages = Math.ceil(sortedProjects.length / itemsPerPage);

  // Slice the sorted projects for pagination
  const paginatedProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedProjects.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedProjects, currentPage]);

  // Build index options for add form if needed
  const indexOptions = unsetIndices.map((idx) => ({ value: idx, label: idx }));

  return (
    <div className="edit-projects-container" ref={containerRef}>
      {/* <h2>Elastic Search Project Manager</h2> */}
      <section className="add-sponsor-section">
        {!adding && !showUnset && !showBroken ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
            }}
          >
            <button
              className="add-user-button"
              onClick={() => setShowUnset(true)}
            >
              <img
                src={activeTab === "projects" ? AddUser : AddUser}
                alt=""
                style={{ marginRight: "10px" }}
              />
              Add Project{" "}
              {unsetIndices.length > 0 ? `(${unsetIndices.length})` : ""}
            </button>
            {brokenProjects.length > 0 && (
              <button
                className="add-user-button"
                onClick={() => setShowBroken(true)}
                style={{ marginLeft: "10px" }}
              >
                <img
                  src={activeTab === "projects" ? AddUser : AddUser}
                  alt=""
                  style={{ marginRight: "10px" }}
                />
                Broken Projects{" "}
                {brokenProjects.length > 0 ? `(${brokenProjects.length})` : ""}
              </button>
            )}
          </div>
        ) : null}

        {/* Add Projects Section */}
        {showUnset && (
          <section className="unset-projects">
            {/* <h3>Add Projects</h3> */}
            {unsetIndices.length > 0 ? (
              <>
                <p>
                  Don’t see the project you want to add? Index the project on{" "}
                  <a
                    href="https://5a3663668f3b480497bc42ff12574d78.us-central1.gcp.cloud.es.io/app/enterprise_search/content/search_indices"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    ElasticSearch
                  </a>
                </p>
                {!adding ? (
                  <>
                    <div className="unset-list">
                      {unsetIndices.map((idx) => (
                        <div
                          key={idx}
                          className="unset-block"
                          onClick={() => startAdd(idx)}
                        >
                          <span className="unset-index">{idx}</span>
                          <span className="unset-label">Add Project</span>
                        </div>
                      ))}
                    </div>
                    <button
                      className="add-user-button"
                      onClick={() => setShowUnset(false)}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <div className="add-project-form">
                    <label>Project Name</label>
                    <input
                      type="text"
                      placeholder="Project Name"
                      value={addForm.PName}
                      onChange={(e) => handleAddChange("PName", e.target.value)}
                    />
                    <label>Project Index</label>
                    {addForm.PIndex ? (
                      <input
                        type="text"
                        value={addForm.PIndex}
                        disabled
                        onChange={() => {}}
                      />
                    ) : (
                      <Select
                        options={indexOptions}
                        value={
                          addForm.PIndex
                            ? { value: addForm.PIndex, label: addForm.PIndex }
                            : null
                        }
                        onChange={(opt) =>
                          handleAddChange("PIndex", opt ? opt.value : "")
                        }
                        placeholder="Select index..."
                      />
                    )}
                    <label>Sponsors</label>
                    <Select
                      isMulti
                      options={sponsors}
                      value={addForm.SPTag}
                      onChange={(selected) =>
                        handleAddChange("SPTag", selected)
                      }
                      placeholder="Select sponsors..."
                    />
                    <div className="field-group timeline-group">
                      <div className="timeline-part">
                        <label>Semester (optional)</label>
                        <Select
                          options={semesterOptions}
                          value={
                            addForm.PTimeline[0]
                              ? {
                                  value: addForm.PTimeline[0],
                                  label: addForm.PTimeline[0],
                                }
                              : null
                          }
                          onChange={(opt) =>
                            handleAddTimelinePart(0, opt ? opt.value : "")
                          }
                          placeholder="Select Semester..."
                          isClearable
                        />
                      </div>
                      <div className="timeline-part">
                        <label>Year (optional)</label>
                        <Select
                          options={yearOptions}
                          value={
                            addForm.PTimeline[1]
                              ? {
                                  value: addForm.PTimeline[1],
                                  label: addForm.PTimeline[1],
                                }
                              : null
                          }
                          onChange={(opt) =>
                            handleAddTimelinePart(1, opt ? opt.value : "")
                          }
                          placeholder="Select Year..."
                          isClearable
                        />
                      </div>
                    </div>
                    <label>Description (optional)</label>
                    <textarea
                      placeholder="Enter project description"
                      value={addForm.PDescription}
                      onChange={(e) =>
                        handleAddChange("PDescription", e.target.value)
                      }
                    />
                    <label>Project Team: (optional)</label>
                    {addForm.PPoC.length > 0 ? (
                      <div className="field list-field">
                        {addForm.PPoC.map((contact, idx) => (
                          <div key={idx} className="list-item">
                            <input
                              type="text"
                              placeholder="Enter Project Team Member"
                              value={contact}
                              onChange={(e) =>
                                handleAddPoCChange(idx, e.target.value)
                              }
                            />
                            <button
                              type="button"
                              className="list-remove-btn"
                              onClick={() => removeAddPoCItem(idx)}
                            >
                              &minus;
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="list-add-btn"
                          onClick={addAddPoCItem}
                        >
                          &#43;
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="add-item-btn"
                        onClick={addAddPoCItem}
                      >
                        + Add Project Team Members
                      </button>
                    )}
                    <label>Sponsor Team: (optional)</label>
                    {addForm.SPPoC.length > 0 ? (
                      <div className="field list-field">
                        {addForm.SPPoC.map((contact, idx) => (
                          <div key={idx} className="list-item">
                            <input
                              type="text"
                              placeholder="Enter Sponsor Team Member"
                              value={contact}
                              onChange={(e) =>
                                handleAddSPPoCChange(idx, e.target.value)
                              }
                            />
                            <button
                              type="button"
                              className="list-remove-btn"
                              onClick={() => removeAddSPPoCItem(idx)}
                            >
                              &minus;
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="list-add-btn"
                          onClick={addAddSPPoCItem}
                        >
                          &#43;
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="add-item-btn"
                        onClick={addAddSPPoCItem}
                      >
                        + Add Sponsor Team Members
                      </button>
                    )}
                    <div className="item-actions">
                      <button
                        type="submit"
                        className="save-btn"
                        onClick={saveNewProject}
                      >
                        Add Project
                      </button>
                      <button className="cancel-btn" onClick={cancelAdd}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p>
                Please index the project you would like to add on{" "}
                <a
                  href="https://5a3663668f3b480497bc42ff12574d78.us-central1.gcp.cloud.es.io/app/enterprise_search/overview"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ElasticSearch
                </a>
                , then refresh this page.
              </p>
            )}
          </section>
        )}
        {/* New: broken-projects section */}
        {showBroken && (
          <section className="broken-projects">
            <h3>Broken Projects</h3>
            {brokenProjects.length > 0 ? (
              brokenProjects.map((project) => {
                const expanded = expandedProjects.includes(project.pid);

                return (
                  <div key={project.pid} className="project-card">
                    {/* header: title + edit/delete icons */}
                    <div
                      className="project-header"
                      onClick={() => toggleExpand(project.pid)}
                    >
                      <div className="project-title">
                        {project.pname}
                        <span className="project-index">
                          {" "}
                          (Index: {project.pindex})
                        </span>
                      </div>
                      <div className="project-actions">
                        <img
                          alt="Edit"
                          className="user-edit-btn"
                          src={EditIcon}
                          onClick={(e) => {
                            e.stopPropagation();
                            enableEdit(project);
                          }}
                        />
                        <img
                          alt="Delete"
                          className="user-delete-btn"
                          src={DeleteIcon}
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmDelete(project);
                          }}
                        />
                      </div>
                    </div>

                    {/* details: either the edit‐form or the readonly info */}
                    {expanded && (
                      <div className="project-details">
                        {editingProjectId === project.pid ? (
                          <div className="project-edit-form">
                            {/* — Project Name */}
                            <label>Project Name</label>
                            <input
                              type="text"
                              value={editForm.PName}
                              onChange={(e) =>
                                handleEditChange("PName", e.target.value)
                              }
                            />

                            {/* — Project Index (disabled) */}
                            <label>Project Index</label>
                            <input
                              type="text"
                              value={editForm.PIndex}
                              onChange={(e) =>
                                handleEditChange("PIndex", e.target.value)
                              }
                              // disable it when *not* in the broken‐projects panel
                              disabled={!showBroken}
                            />

                            {/* — Sponsors */}
                            <label>Sponsors</label>
                            <Select
                              isMulti
                              options={sponsors}
                              value={editForm.SPTag}
                              onChange={(sel) => handleEditChange("SPTag", sel)}
                              placeholder="Select sponsors..."
                            />

                            {/* — Timeline (semester + year) */}
                            <div className="field-group timeline-group">
                              <div style={{ flex: 1 }}>
                                <label>Semester</label>
                                <Select
                                  options={semesterOptions}
                                  value={
                                    editForm.PTimeline[0]
                                      ? {
                                          value: editForm.PTimeline[0],
                                          label: editForm.PTimeline[0],
                                        }
                                      : null
                                  }
                                  onChange={(opt) =>
                                    handleEditTimelinePart(0, opt?.value || "")
                                  }
                                  isClearable
                                />
                              </div>
                              <div style={{ flex: 1 }}>
                                <label>Year</label>
                                <Select
                                  options={yearOptions}
                                  value={
                                    editForm.PTimeline[1]
                                      ? {
                                          value: editForm.PTimeline[1],
                                          label: editForm.PTimeline[1],
                                        }
                                      : null
                                  }
                                  onChange={(opt) =>
                                    handleEditTimelinePart(1, opt?.value || "")
                                  }
                                  isClearable
                                />
                              </div>
                            </div>

                            {/* — Description */}
                            <label>Description</label>
                            <textarea
                              value={editForm.PDescription}
                              onChange={(e) =>
                                handleEditChange("PDescription", e.target.value)
                              }
                            />

                            {/* — Project Team (PPoC) */}
                            <label>Project Team:</label>
                            {editForm.PPoC.length > 0 ? (
                              <div className="field list-field">
                                {editForm.PPoC.map((c, i) => (
                                  <div key={i} className="list-item">
                                    <input
                                      type="text"
                                      value={c}
                                      onChange={(e) =>
                                        handleEditPoCChange(i, e.target.value)
                                      }
                                    />
                                    <button
                                      className="list-remove-btn"
                                      onClick={() => removeEditPoCItem(i)}
                                    >
                                      &minus;
                                    </button>
                                  </div>
                                ))}
                                <button
                                  className="list-add-btn"
                                  onClick={addEditPoCItem}
                                >
                                  &#43;
                                </button>
                              </div>
                            ) : (
                              <button
                                className="add-item-btn"
                                onClick={addEditPoCItem}
                              >
                                + Add Project Team Members
                              </button>
                            )}

                            {/* — Sponsor Team (SPPoC) */}
                            <label>Sponsor Team:</label>
                            {editForm.SPPoC.length > 0 ? (
                              <div className="field list-field">
                                {editForm.SPPoC.map((c, i) => (
                                  <div key={i} className="list-item">
                                    <input
                                      type="text"
                                      value={c}
                                      onChange={(e) =>
                                        handleEditSPPoCChange(i, e.target.value)
                                      }
                                    />
                                    <button
                                      className="list-remove-btn"
                                      onClick={() => removeEditSPPoCItem(i)}
                                    >
                                      &minus;
                                    </button>
                                  </div>
                                ))}
                                <button
                                  className="list-add-btn"
                                  onClick={addEditSPPoCItem}
                                >
                                  &#43;
                                </button>
                              </div>
                            ) : (
                              <button
                                className="add-item-btn"
                                onClick={addEditSPPoCItem}
                              >
                                + Add Sponsor Team Members
                              </button>
                            )}

                            {/* — Save / Cancel */}
                            <div className="item-actions">
                              <button className="save-btn" onClick={saveEdits}>
                                Update Project
                              </button>
                              <button
                                className="cancel-btn"
                                onClick={cancelEdit}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="project-info">
                            <p>
                              <strong>Sponsors:</strong>{" "}
                              {project.sptag?.join(", ") || "None"}
                            </p>
                            <p>
                              <strong>Timeline:</strong>{" "}
                              {Array.isArray(project.ptimeline) &&
                              project.ptimeline.length === 2
                                ? `Semester: ${project.ptimeline[0]}, Year: ${project.ptimeline[1]}`
                                : "None"}
                            </p>
                            <p>
                              <strong>Description:</strong>{" "}
                              {project.pdescription || "None"}
                            </p>
                            <p>
                              <strong>Project Team:</strong>{" "}
                              {project.ppoc?.length ? (
                                <ul className="timeline-list">
                                  {project.ppoc.map((p, i) => (
                                    <li key={i}>{p}</li>
                                  ))}
                                </ul>
                              ) : (
                                "None"
                              )}
                            </p>
                            <p>
                              <strong>Point of Contact:</strong>{" "}
                              {project.sppoc?.length ? (
                                <ul className="timeline-list">
                                  {project.sppoc.map((p, i) => (
                                    <li key={i}>{p}</li>
                                  ))}
                                </ul>
                              ) : (
                                "None"
                              )}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <p>No broken projects found.</p>
            )}

            <button
              className="add-user-button"
              onClick={() => setShowBroken(false)}
            >
              Close
            </button>
          </section>
        )}
      </section>

      {/* Current Projects Section */}
      <section className="current-projects">
        <div className="project-filters" style={{ marginBottom: "20px" }}>
          <div className="project-filter">
            <label>Filter by Sponsors: </label>
            <Select
              isMulti
              options={sponsors}
              value={sponsorFilter}
              onChange={(selected) => setSponsorFilter(selected || [])}
              placeholder="Select sponsors..."
              className="project-select"
            />
          </div>
          <div className="project-filter">
            <label>Filter by Semester: </label>
            <Select
              options={semesterOptions}
              value={semesterFilter}
              onChange={(opt) => setSemesterFilter(opt)}
              placeholder="Any Semester..."
              isClearable
              className="project-select"
            />
          </div>
          <div className="project-filter">
            <label>Filter by Year: </label>
            <Select
              options={yearOptions}
              value={yearFilter}
              onChange={(opt) => setYearFilter(opt)}
              placeholder="Any Year..."
              isClearable
              className="project-select"
            />
          </div>
        </div>
        {sortedProjects.length === 0 ? (
          <p>No Current Projects matching your filters.</p>
        ) : (
          // Use paginatedProjects here so that only 20 projects per page are rendered.
          paginatedProjects.map((project) => {
            const expanded = expandedProjects.includes(project.pid);
            return (
              <div key={project.pid} className="project-card">
                <div
                  className="project-header"
                  onClick={() => toggleExpand(project.pid)}
                >
                  <div className="project-title">
                    {project.pname}
                    <span className="project-index">
                      {" "}
                      (Index: {project.pindex})
                    </span>
                  </div>
                  <div className="project-actions">
                    <img
                      alt="Edit"
                      className="user-edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        enableEdit(project);
                      }}
                      src={EditIcon}
                    />
                    <img
                      alt="Delete"
                      className="user-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDeleteProject({
                          pid: project.pid,
                          pname: project.pname,
                        });
                        setDeleteInput("");
                      }}
                      src={DeleteIcon}
                    />
                  </div>
                </div>
                {expanded && (
                  <div className="project-details">
                    {editingProjectId === project.pid ? (
                      <div className="project-edit-form">
                        <label>Project Name</label>
                        <input
                          type="text"
                          value={editForm.PName}
                          onChange={(e) =>
                            handleEditChange("PName", e.target.value)
                          }
                        />
                        <label>Project Index</label>
                        <input
                          type="text"
                          value={editForm.PIndex}
                          disabled
                          onChange={() => {}}
                        />
                        <label>Sponsors</label>
                        <Select
                          isMulti
                          options={sponsors}
                          value={editForm.SPTag}
                          onChange={(selected) =>
                            handleEditChange("SPTag", selected)
                          }
                          placeholder="Select sponsors..."
                        />
                        <div className="field-group timeline-group">
                          <div style={{ flex: 1 }}>
                            <label>Semester</label>
                            <Select
                              options={semesterOptions}
                              value={
                                editForm.PTimeline[0]
                                  ? {
                                      value: editForm.PTimeline[0],
                                      label: editForm.PTimeline[0],
                                    }
                                  : null
                              }
                              onChange={(opt) =>
                                handleEditTimelinePart(0, opt ? opt.value : "")
                              }
                              placeholder="Select Semester..."
                              isClearable
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label>Year</label>
                            <Select
                              options={yearOptions}
                              value={
                                editForm.PTimeline[1]
                                  ? {
                                      value: editForm.PTimeline[1],
                                      label: editForm.PTimeline[1],
                                    }
                                  : null
                              }
                              onChange={(opt) =>
                                handleEditTimelinePart(1, opt ? opt.value : "")
                              }
                              placeholder="Select Year..."
                              isClearable
                            />
                          </div>
                        </div>
                        <label>Description</label>
                        <textarea
                          value={editForm.PDescription}
                          onChange={(e) =>
                            handleEditChange("PDescription", e.target.value)
                          }
                        />
                        <label>Project Team: (optional)</label>
                        {editForm.PPoC.length > 0 ? (
                          <div className="field list-field">
                            {editForm.PPoC.map((contact, idx) => (
                              <div key={idx} className="list-item">
                                <input
                                  type="text"
                                  placeholder="Enter Project Team Member"
                                  value={contact}
                                  onChange={(e) =>
                                    handleEditPoCChange(idx, e.target.value)
                                  }
                                />
                                <button
                                  type="button"
                                  className="list-remove-btn"
                                  onClick={() => removeEditPoCItem(idx)}
                                >
                                  &minus;
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              className="list-add-btn"
                              onClick={addEditPoCItem}
                            >
                              &#43;
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="add-item-btn"
                            onClick={addEditPoCItem}
                          >
                            + Add Project Team Members
                          </button>
                        )}
                        <label>Sponsor Team: (optional)</label>
                        {editForm.SPPoC.length > 0 ? (
                          <div className="field list-field">
                            {editForm.SPPoC.map((contact, idx) => (
                              <div key={idx} className="list-item">
                                <input
                                  type="text"
                                  placeholder="Enter Sponsor Team Member"
                                  value={contact}
                                  onChange={(e) =>
                                    handleEditSPPoCChange(idx, e.target.value)
                                  }
                                />
                                <button
                                  type="button"
                                  className="list-remove-btn"
                                  onClick={() => removeEditSPPoCItem(idx)}
                                >
                                  &minus;
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              className="list-add-btn"
                              onClick={addEditSPPoCItem}
                            >
                              &#43;
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="add-item-btn"
                            onClick={addEditSPPoCItem}
                          >
                            + Add Sponsor Team Members
                          </button>
                        )}
                        <div className="item-actions">
                          <button
                            type="submit"
                            className="save-btn"
                            onClick={saveEdits}
                          >
                            Update Project
                          </button>
                          <button className="cancel-btn" onClick={cancelEdit}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="project-info">
                        <p>
                          <strong>Sponsors:</strong>{" "}
                          {project.sptag ? project.sptag.join(", ") : ""}
                        </p>
                        <div>
                          <strong>Timeline:</strong>{" "}
                          {Array.isArray(project.ptimeline) &&
                          project.ptimeline.length === 2
                            ? `Semester: ${project.ptimeline[0]}, Year: ${project.ptimeline[1]}`
                            : "None"}
                        </div>
                        <p>
                          <strong>Description:</strong>{" "}
                          {project.pdescription || "None"}
                        </p>
                        {project.ppoc && project.ppoc.length > 0 ? (
                          <div>
                            <strong>Project Team:</strong>
                            <ul className="timeline-list">
                              {project.ppoc.map((person, idx) => (
                                <li key={idx}>{person}</li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <p>
                            <strong>Project Team:</strong> None
                          </p>
                        )}
                        {project.sppoc && project.sppoc.length > 0 ? (
                          <div>
                            <strong>Point of Contact:</strong>
                            <ul className="timeline-list">
                              {project.sppoc.map((contact, idx) => (
                                <li key={idx}>{contact}</li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <p>
                            <strong>Point of Contact:</strong> None
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div
            className="pagination-controls"
            style={{
              marginBottom: "20px",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              src={currentPage !== 1 ? NextPageDark : NextPage}
              onClick={() => setCurrentPage((prev) => prev - 1)}
              alt="Previous Page"
              className="workspace-icon"
              style={{ transform: "scaleX(-1)" }}
            />
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <img
              src={currentPage !== totalPages ? NextPageDark : NextPage}
              onClick={() => setCurrentPage((prev) => prev + 1)}
              alt="Next Page"
              className="workspace-icon"
            />
          </div>
        )}
      </section>

      {/* Delete Confirmation Modal */}
      {confirmDeleteProject && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h4>Confirm Deletion</h4>
            <p>
              Type <strong>{confirmDeleteProject.pname}</strong> to confirm
              deletion:
            </p>
            <input
              type="text"
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
            />
            <div className="delete-actions">
              <button
                className="delete-confirm-btn"
                onClick={performDelete}
                disabled={deleteInput !== confirmDeleteProject.pname}
              >
                Delete
              </button>
              <button
                className="cancel-btn"
                onClick={() => setConfirmDeleteProject(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditProjects;
